import styled from 'styled-components'
import { Box, Text } from '../../../core-components'
import { ThemeColors, ThemeShadows } from '../../../theme'
import { EndContentIcon } from '../../data/end-content/EndContent'
import { StartContentIcon } from '../../data/start-content/StartContent'

export type BaseButtonVariant = 'major' | 'moderate' | 'minor' | 'cautionHigh' | 'cautionLow'

export const BaseButtonIcon = StartContentIcon
export const BaseButtonEndIcon = EndContentIcon

/** Internal design system component for sharing styles between Button and OptionButton */
export const BaseButtonBox = styled(Box)<{
  UNSAFE_hexColor?: string
  endIconColor?: ThemeColors
  endIconHoverColor?: ThemeColors
  hoverBg: ThemeColors
  hoverColor?: ThemeColors
  iconColor: ThemeColors
  iconHoverColor: ThemeColors
}>`
  transition:
    background-color 0.1s ease-in-out,
    color 0.1s ease-in-out;
  cursor: pointer;
  text-decoration: none;
  ${({ UNSAFE_hexColor }) => (UNSAFE_hexColor ? `background: ${UNSAFE_hexColor};` : '')}

  ${BaseButtonIcon} {
    color: ${({ iconColor, theme }) => theme.colors[iconColor]};
  }

  ${BaseButtonEndIcon} {
    color: ${({ endIconColor, theme }) => endIconColor && theme.colors[endIconColor]};
  }

  :hover,
  :disabled,
  &[aria-disabled='true'] {
    ::after {
      box-shadow: none;
    }
  }

  :hover,
  [data-x='y'] /* we need anything here because otherwise storybook-addon-pseudostates doesn't work in OptionButton dark mode variant major (no idea why) */ {
    background: ${({ hoverBg, theme }) => theme.colors[hoverBg]};
    ${({ UNSAFE_hexColor }) => (UNSAFE_hexColor ? `background: ${UNSAFE_hexColor}CC;` : '')}

    ${Text} {
      color: ${({ hoverColor, theme }) => hoverColor && theme.colors[hoverColor]};
    }

    ${BaseButtonIcon} {
      color: ${({ iconHoverColor, theme }) => theme.colors[iconHoverColor]};
    }

    ${BaseButtonEndIcon} {
      color: ${({ endIconHoverColor, theme }) =>
        endIconHoverColor && theme.colors[endIconHoverColor]};
    }
  }

  :focus-visible {
    outline: none;
    // We can't move it to ::after shadow hack (see Box) because not all buttons have shadows
    box-shadow: ${({ theme }) => theme.shadows['border/focus-indicator']} !important;
    background: ${({ hoverBg, theme }) => theme.colors[hoverBg]};

    ::after {
      box-shadow: none;
    }

    ${Text} {
      color: ${({ hoverColor, theme }) => hoverColor && theme.colors[hoverColor]};
    }

    ${BaseButtonIcon} {
      color: ${({ iconHoverColor, theme }) => theme.colors[iconHoverColor]};
    }

    ${BaseButtonEndIcon} {
      color: ${({ endIconHoverColor, theme }) =>
        endIconHoverColor && theme.colors[endIconHoverColor]};
    }
  }

  :disabled,
  &[aria-disabled='true'] {
    cursor: default;
    background: ${({ theme }) => theme.colors['bg/disabled']};

    ${Text} {
      color: ${({ theme }) => theme.colors['fg/disabled']};
    }

    ${BaseButtonIcon}, ${BaseButtonEndIcon} {
      color: ${({ theme }) => theme.colors['fg/disabled']};
    }
  }
`

export const baseButtonVariantProps: Record<
  BaseButtonVariant,
  {
    bg: ThemeColors
    border?: ThemeShadows
    color: ThemeColors
    hoverBg: ThemeColors
    hoverColor: ThemeColors
    iconColor: ThemeColors
    iconHoverColor: ThemeColors
    endIconColor?: ThemeColors
    endIconHoverColor?: ThemeColors
    endIconFocusColor?: ThemeColors
  }
> = {
  major: {
    bg: 'bg/button-major-default',
    color: 'fg/major-on-color',
    hoverBg: 'bg/button-major-hovered',
    hoverColor: 'fg/major-on-color',
    iconColor: 'fg/major-on-color',
    endIconColor: 'fg/major-on-color',
    endIconHoverColor: 'fg/major-on-color',
    iconHoverColor: 'fg/major-on-color',
  },
  moderate: {
    bg: 'bg/button-moderate-default',
    color: 'fg/major',
    hoverBg: 'bg/button-moderate-hovered',
    hoverColor: 'fg/major',
    iconColor: 'fg/major',
    endIconColor: 'fg/major',
    endIconHoverColor: 'fg/major',
    iconHoverColor: 'fg/major',
  },
  minor: {
    bg: 'bg/button-minor-default',
    border: 'border/on-major',
    color: 'fg/moderate',
    hoverBg: 'bg/button-minor-hovered',
    hoverColor: 'fg/moderate',
    iconColor: 'fg/moderate',
    endIconColor: 'fg/moderate',
    endIconHoverColor: 'fg/moderate',
    iconHoverColor: 'fg/moderate',
  },
  cautionHigh: {
    bg: 'bg/button-caution-default',
    color: 'fg/major-on-color',
    hoverBg: 'bg/button-caution-hovered',
    hoverColor: 'fg/major-on-color',
    iconColor: 'fg/major-on-color',
    endIconColor: 'fg/major-on-color',
    endIconHoverColor: 'fg/major-on-color',
    iconHoverColor: 'fg/major-on-color',
  },
  cautionLow: {
    bg: 'bg/button-minor-default',
    border: 'border/on-major',
    color: 'fg/caution',
    hoverBg: 'bg/button-caution-hovered',
    hoverColor: 'fg/major-on-color',
    iconColor: 'fg/caution',
    iconHoverColor: 'fg/major-on-color',
    endIconColor: 'fg/caution',
    endIconHoverColor: 'fg/major-on-color',
    endIconFocusColor: 'fg/major-on-color',
  },
}

export const baseButtonProps = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  m: 0,
  border: 'none',
  borderRadius: 10,
  px: 4,
  py: 3,
  w: 'fit-content',
} as const

export const getCommonProps = ({
  variant,
  isFullWidth,
  size,
}: {
  variant: BaseButtonVariant
  isFullWidth?: boolean
  size?: 7 | 8
}) => {
  const {
    color,
    bg,
    border,
    hoverColor,
    hoverBg,
    iconColor,
    iconHoverColor,
    endIconColor,
    endIconHoverColor,
    endIconFocusColor,
  } = baseButtonVariantProps[variant]

  return {
    ...baseButtonProps,
    // To keep consistent with button - https://html.spec.whatwg.org/multipage/rendering.html#button-layout
    // > If the computed value of 'inline-size' is 'auto', then the used value is the fit-content inline size
    w: isFullWidth ? '100%' : 'fit-content',
    borderRadius: size === 7 ? 8 : 10,
    px: size === 7 ? 3 : 4,
    py: size === 7 ? 2 : 3,
    bg,
    boxShadow: border,
    color,
    hoverColor,
    hoverBg,
    endIconColor,
    endIconHoverColor,
    endIconFocusColor,
    iconColor,
    iconHoverColor,
  } as const
}
